import type {ReactElement} from 'react';
import {useContext, useState} from 'react';
import {StylesInputPanelSection} from '@Panels/styles-input-panel/styles-input-panel.types';
import {BrandKitsSection} from '@Panels/styles-input-panel/components/brand-kits-section/brand-kits-section';
import {Text, TextSize} from '@Components/text';
import {ModalDetails} from '@Components/modal/modal-context';
import {ColorFill} from '@Components/color-fill';
import {openUserMediaModal} from '@Modals/user-media-modal';
import {UploadsMediaSubTab, UploadsMediaType} from '@Panels/user-media-panel/user-media-panel.types';
import ClickableDiv from '@Components/clickable-div/clickable-div';
import type {ImageData} from '@Libraries/add-media-library';
import {repoImageThumbURL} from '@Libraries/graphic-item-image-library';
import {isFontSupported} from '@Libraries/font-library';
import {ContextualMenuFontButton} from '@Components/contextual-menu/components/contextual-menu-font-button';
import {useAreFontsLoading} from '@Hooks/font-selector/useAreFontsLoading';
import {Button, Size, Type} from '@Components/button';
import {LabelledCheckbox} from '@Components/labelled-checkbox';
import {CHECKBOX_TYPE} from '@Components/checkbox';
import {getHashedFileNameFromImageURL} from '@Libraries/user-image-library';
import {useDoesUserHaveBrandKits} from '@Hooks/brands/useDoesUserHaveBrandKits';
import {BRANDS_LOADING_STATES, canUserAccessBrandKits} from '@Libraries/brands-library';
import {setBrandsLoadingState} from '@Components/mystuff-brands/brands-reducer';
import {useActiveBrandId} from '@Hooks/brands/useActiveBrandId';
import {useBrandKitWithAssets} from '@Hooks/brands/useBrandKitWithAssets';
import type {StylesInputPanelDefaultSectionProps} from './styles-input-panel-default-section.types';
import styles from './styles-input-panel-default-section.module.scss';
import {useAppDispatch} from '@/hooks';
import {hexToRgba} from '@Utils/color.util';

export function StylesInputPanelDefaultSection({
  changeSection,
  closePanel,
  selectedColorHex,
  selectedFontFamily,
  selectedImageURL,
  onApplyBrandKit,
  onSelectImage,
  onImageUploadStart,
  allowImageSelection = true,
  className = '',
}: StylesInputPanelDefaultSectionProps): ReactElement<any> {
  const modalDetails = useContext(ModalDetails);
  const isInModal = modalDetails !== null;
  const areFontsLoading = useAreFontsLoading();
  const activeBrandId = useActiveBrandId();
  const activeBrandKitWithAssets = useBrandKitWithAssets(activeBrandId ?? '');
  const [isBrandKitsOptionChecked, setIsBrandKitsOptionChecked] = useState(true);
  const doesUserHaveBrandKits = useDoesUserHaveBrandKits();
  const dispatch = useAppDispatch();

  const onClickBrandKit = (color?: string, font?: string, image?: string): void => {
    if (onApplyBrandKit) {
      onApplyBrandKit(color, font, image);
    }
  };

  const getTopSpacing = (): string => {
    return isInModal ? 'spacing-m-t-8' : 'spacing-m-t-10';
  };

  const getBottomBorderClasses = (): string => {
    return 'border-s-standard _no-border-top _no-border-right _no-border-left';
  };

  const changeToColorSection = (): void => {
    changeSection(StylesInputPanelSection.COLORS);
  };

  const changeToFontsSection = (): void => {
    changeSection(StylesInputPanelSection.FONTs);
  };

  const onAddSelected = (images: ImageData[], currentUploadIndex?: number): void => {
    if (currentUploadIndex && currentUploadIndex > 1) {
      return;
    }

    if (!onSelectImage) {
      return;
    }

    onSelectImage(repoImageThumbURL(images[0].hashedFilename, images[0].extension));
  };

  const onLogoUploadClick = (): void => {
    const shouldShowLogosByDefault = activeBrandKitWithAssets && activeBrandKitWithAssets.logos.length > 0;

    openUserMediaModal({
      onAddSelectedCTAText: window.i18next.t('pmwjs_add_logo'),
      maxSelectionNumber: 1,
      defaultTab: UploadsMediaType.IMAGE,
      mediaTypesToShow: [UploadsMediaType.IMAGE],
      uploadAcceptedMimetype: 'image/*',
      defaultActiveSubTab: shouldShowLogosByDefault ? UploadsMediaSubTab.LOGO : UploadsMediaSubTab.UPLOADS,
      onStartAddSelected: onImageUploadStart,
      onAddSelected,
    });
  };

  const renderLogosRow = (): ReactElement<any> | null => {
    if (!allowImageSelection) {
      return null;
    }

    return (
      <li className="flex-row-align-center flex-justify-between _overflow-hidden spacing-m-b-5 _unpadded">
        <Text val={window.i18next.t('pmwjs_logo')} className="content-sub-text flex-1 -truncated-text -capitalizedtext" size={TextSize.XSMALL} />

        <ClickableDiv
          className={`${styles.previewContainer} ${styles.imgPreviewContainer} flex-center border-s-dark radius-4 _overflow-hidden _cursor-pointer`}
          onClick={onLogoUploadClick}
        >
          {selectedImageURL ? <img src={selectedImageURL} alt={window.i18next.t('pmwjs_logo')} className={styles.imgPreview} /> : null}
        </ClickableDiv>
      </li>
    );
  };

  const onDoneBtnClick = (): void => {
    if (canUserAccessBrandKits() && isBrandKitsOptionChecked && !doesUserHaveBrandKits) {
      dispatch(setBrandsLoadingState(BRANDS_LOADING_STATES.NOT_LOADED));
      void window.PMW.writeLocal('brand/createBrandKitWithAssets', {
        name: window.i18next.t('pmwjs_new_brand'),
        hexCode: selectedColorHex.substring(1),
        fontFamily: selectedFontFamily,
        imageHashedFileName: selectedImageURL ? getHashedFileNameFromImageURL(selectedImageURL) : undefined,
      });
    }
    closePanel();
  };

  return (
    <div className={`flex-v-row ${className}`}>
      <BrandKitsSection onClickBrandKit={onClickBrandKit} allowImageSelection={allowImageSelection} />
      <div className={`_full-width flex-v-row ${isInModal ? 'spacing-p-r-4' : 'spacing-p-r-5'}`}>
        <Text val={window.i18next.t('pmwjs_styles')} bold size={TextSize.XSMALL} className={`spacing-p-b-2 content-body spacing-m-b-5 ${getBottomBorderClasses()}`} />

        <ul className="flex-v-row _unpadded">
          {renderLogosRow()}

          <li className="flex-row-align-center flex-justify-between _overflow-hidden spacing-m-b-5 _unpadded">
            <Text val={window.i18next.t('color')} className="content-sub-text flex-1 -truncated-text -capitalizedtext" size={TextSize.XSMALL} />
            <div className={`${styles.previewContainer} flex-center border-s-dark radius-4 _overflow-hidden`}>
              <ColorFill color={hexToRgba(selectedColorHex)} className={`_full-width _full-height _borderless ${styles.noBorderRadius}`} onClick={changeToColorSection} />
            </div>
          </li>

          <ContextualMenuFontButton
            id="select-font-button"
            label={window.i18next.t('pmwjs_heading_font')}
            selectedFontFamily={selectedFontFamily}
            onClickFontButton={changeToFontsSection}
            isFontSuppported={isFontSupported(selectedFontFamily) && !areFontsLoading}
          />
        </ul>

        <div className={`flex-v-row ${getTopSpacing()}`}>
          {canUserAccessBrandKits() && !doesUserHaveBrandKits ? (
            <LabelledCheckbox
              className="spacing-m-b-3"
              checkboxType={CHECKBOX_TYPE.PRIMARY}
              label={window.i18next.t('pmws_save_style_as_a_new_brand_kit')}
              isChecked={isBrandKitsOptionChecked}
              onChange={setIsBrandKitsOptionChecked}
            />
          ) : null}
          <Button text={window.i18next.t('pmwjs_done')} size={Size.SMALL} type={Type.PRIMARY} onClick={onDoneBtnClick} />
        </div>
      </div>
    </div>
  );
}
